import { Clipboard } from '@angular/cdk/clipboard';
import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AudioSegment, LcsAudioPlayerComponent } from '@lcs/shared/audio-player';
import { LcsResponsiveTableComponent } from '@lcs/shared/table';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
   CallDetail,
   CallSegment,
   ExtensionTypes,
   QueueAgentDial,
   QueueCallbackDial,
   QueueHistory,
   QueueHistoryCallDetail,
   QueueHistoryDisposition,
} from 'apollo-angular/graphql-types';
import { CallDirectionTypes } from 'generated/graphql-types';
import { EMPTY, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { getProviderDisplayName } from 'src/app/common-components/helpers/provider-helper';
import { AccountDetailApiService } from 'src/app/pages/account-details/services/account-detail-api.service';
import { ActiveCallsRegisterListItem } from 'src/app/pages/active-calls/models/active-calls-register-results.model';
import { CallRecordingsPrivileges } from 'src/app/pages/active-calls/models/call-recordings-privileges';
import { ActiveCallRegisterType } from 'src/app/pages/active-calls/services/active-calls-register-session-storage.service';
import { getCallRecordingsPrivileges } from 'src/app/privileges/privileges.selector';
import { getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import { saveExpandCallDetailAdvancedSection } from 'src/app/store/user-preferences/user-preferences.actions';
import { getExpandCallDetailAdvancedSectionUserPreference } from 'src/app/store/user-preferences/user-preferences.selectors';
import { AppConfigService } from '../../app-config/app-config-service';
import * as AccountActiveCallsRegisterActions from '../../pages/active-calls/state/account-active-calls/account-active-calls.actions';
import * as SystemActiveCallsRegisterActions from '../../pages/active-calls/state/system-active-calls/system-active-calls.actions';
import { setHideRecordingDate } from '../helpers/call-logs-helper';
import { provideAudioBlobSourceResolver } from './audio-blob-source-resolver';
import {
   AgentSegmentDial,
   CallbackRequestedSegmentDial,
   CallbackSegmentDial,
   CallSegmentRow,
   SegmentDial,
   TableRow,
} from './call-segment-with-parent-call-detail-model';
import { NavigatableDialog } from './navigatable-dialog';

@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   selector: 'ndt-call-detail-dialog',
   styleUrls: ['./call-detail-dialog.component.scss'],
   templateUrl: './call-detail-dialog.component.html',
   standalone: false,
   // The recording endpoint doesn't support HTTP range requests, so seeking against it directly
   // re-downloads the whole file on every seek (see audio-blob-source-resolver.ts).
   providers: [provideAudioBlobSourceResolver()],
})
export class CallDetailDialogComponent extends NavigatableDialog implements OnDestroy {
   callLogs: any;

   callSegmentsAndRecording: any;

   relatedCallDetails: CallDetail[];

   allQueueHistoryItems: QueueHistoryCallDetail[];

   columns: string[];

   isSystemUser: boolean;

   canHangupCalls: boolean;

   allCallLogIds: number[];

   hideRecordingDate: string | null = null;

   private destroyed$ = new Subject<void>();

   registerType: ActiveCallRegisterType;

   @ViewChild('responsiveTable') responsiveTable: LcsResponsiveTableComponent;

   @ViewChild('audioPlayer') audioPlayerRef: LcsAudioPlayerComponent;

   callSegmentsWithAssociatedCallDetail: CallSegmentRow[] = [];

   isPhoneCallThatWentDirectlyToSourceExtension: boolean = false;

   flattenedRows: TableRow[] = [];

   get entityId(): number | null {
      return this.callSegmentsAndRecording?.callDetailId ?? this.callLogs?.callDetailId;
   }

   get allRelatedCallDetailIds(): number[] {
      const currentId = this.callSegmentsAndRecording?.callDetailId ?? this.callLogs?.callDetailId;
      const relatedIds = this.callSegmentsAndRecording
         ? this.relatedCallDetails?.filter((c) => c.endTime != null).map((c) => c.callDetailId)
         : this.relatedCallDetails
              ?.filter((c) => c.endTime != null && (this.allCallLogIds ?? []).includes(c.callDetailId))
              .map((c) => c.callDetailId);

      return [...(currentId != null ? [currentId] : []), ...(relatedIds ?? [])].sort((a, b) => a - b);
   }

   get canShowRecordingFile(): boolean {
      const withinRetention = (startTime: Date) =>
         this.hideRecordingDate === null || startTime.toString() >= this.hideRecordingDate;
      const canView = !this.isSystemUser || this.callRecordingsPrivileges?.enabled;
      if (
         this.callSegmentsAndRecording?.callRecording?.file &&
         withinRetention(this.callSegmentsAndRecording.startTime) &&
         canView &&
         !this.isPhoneCallThatWentDirectlyToSourceExtension
      ) {
         return true;
      }
      if (
         this.callLogs?.callRecordingFile &&
         withinRetention(this.callLogs.startTime) &&
         canView &&
         !this.isPhoneCallThatWentDirectlyToSourceExtension
      ) {
         return true;
      }
      return false;
   }

   get recordingFile(): { fileToken: string } | null {
      return this.callSegmentsAndRecording?.callRecording?.file ?? this.callLogs?.callRecordingFile ?? null;
   }

   get recordingSegments(): AudioSegment[] | null {
      const ownSegments = this.callSegmentsWithAssociatedCallDetail.filter(
         (segment) => segment.parentCallDetailId === this.entityId
      );
      if (ownSegments.length === 0) {
         return null;
      }

      const audioSegments: AudioSegment[] = [];

      const handoffSegment = this.callSegmentsWithAssociatedCallDetail.find(
         (segment) => segment.answeredCallDetailId === this.entityId
      );
      if (handoffSegment?.answeredCallBoundaryTime) {
         const leadingGapSeconds =
            (new Date(ownSegments[0].startDate).getTime() -
               new Date(handoffSegment.answeredCallBoundaryTime).getTime()) /
            1000;
         if (isFinite(leadingGapSeconds) && leadingGapSeconds > 1) {
            audioSegments.push({
               durationSeconds: leadingGapSeconds,
               tooltip: this.getSegmentDescription(handoffSegment),
            });
         }
      }

      audioSegments.push(
         ...ownSegments.map((segment) => ({
            durationSeconds: segment.duration ?? 0,
            tooltip: this.getSegmentDescription(segment),
         }))
      );
      return audioSegments;
   }

   private getSegmentDescription(segment: CallSegmentRow): string | undefined {
      const extension =
         this.callLogs.callDirectionTypeId === 'Outbound' && segment.sourceExtensionId === segment.extensionId
            ? segment.sourceExtension
            : segment.extension;
      return extension?.name ?? undefined;
   }

   currentAccount$: Observable<number | null>;

   callRecordingsPrivileges: CallRecordingsPrivileges;

   advancedSectionExpanded$: Observable<boolean>;

   constructor(
      private dialogRef: MatDialogRef<CallDetailDialogComponent>,
      @Inject(MAT_DIALOG_DATA) private data: any,
      private datePipe: DatePipe,
      private appConfig: AppConfigService,
      public store: Store,
      private actions$: Actions,
      public changeDetectorRef: ChangeDetectorRef,
      private clipboard: Clipboard,
      private snackBar: MatSnackBar,
      private accountDetailApiService: AccountDetailApiService
   ) {
      super();
      this.callLogs = data.callLogs;
      this.callSegmentsAndRecording = data.callData;
      if (data.callData) {
         this.isPhoneCallThatWentDirectlyToSourceExtension =
            this.getIsPhoneCallThatWentDirectlyToSourceExtensionFromCallData(data.callData);
      } else if (data.callLogs) {
         this.isPhoneCallThatWentDirectlyToSourceExtension = data.callLogs.isPhoneCallThatWentDirectlyToSourceExtension;
      }
      this.relatedCallDetails = data.relatedCallDetails ?? data.callLogs?.relatedCallDetails;
      this.isSystemUser = data.isSystemUser;
      this.canHangupCalls = data.canHangupCalls.enabled;
      this.allCallLogIds = data.allCallLogIds;
      this.registerType = data.registerType;
      this.columns = ['Extension', 'Type', 'Description', 'Start Time', 'Duration'];

      this.actions$
         .pipe(
            ofType(
               AccountActiveCallsRegisterActions.hangupActiveCallSuccess,
               SystemActiveCallsRegisterActions.hangupActiveCallSuccess
            ),
            switchMap(() => {
               this.dialogRef.close();
               return EMPTY;
            }),
            takeUntil(this.destroyed$)
         )
         .subscribe();

      this.navigateDown.subscribe((newId: number) => {
         this.dialogRef.close({ navigateTo: newId });
      });
      this.navigateUp.subscribe((newId: number) => {
         this.dialogRef.close({ navigateTo: newId });
      });
      store
         .pipe(select(getCallRecordingsPrivileges))
         .pipe(takeUntil(this.destroyed$))
         .subscribe((privileges) => {
            this.callRecordingsPrivileges = privileges;
         });
      this.advancedSectionExpanded$ = this.store.pipe(
         select(getExpandCallDetailAdvancedSectionUserPreference),
         map((expanded) => expanded ?? false)
      );
      this.currentAccount$ = this.store.select(getSelectedAccountId);
      this.currentAccount$
         .pipe(
            filter((accountId: number | null): accountId is number => {
               return accountId != null;
            })
         )
         .subscribe((accountId: number) => {
            this.fetchHideRecordingDate(accountId);
         });
   }

   ngAfterViewInit() {
      let showAlertColumn = false;
      this.callSegmentsWithAssociatedCallDetail?.forEach((segment) => {
         if (segment.hasDials) {
            this.columns.unshift('expand');
         }
         if (segment.dials?.some((dial) => dial.type === 'callback')) {
            showAlertColumn = true;
         }
      });
      if (showAlertColumn) {
         this.columns.push('alert');
      }
   }

   ngOnInit() {
      this.buildCallSegmentsWithAssociatedCallDetails();
   }

   fileNotFound = false;

   onFileNotFound(_event?: ErrorEvent) {
      this.fileNotFound = true;
   }
   get homerSipCallIdLogsLink(): string {
      const homerUrl = this.appConfig.getConfig()?.homerUrl;
      let link =
         `https://${homerUrl}/search/result?%7B%22timestamp%22%3A%7B%22from%22%3A` +
         this.adjustedStartTimeDate +
         '%2C%22to%22%3A' +
         (this.callLogs.endTime ? this.adjustedEndTimeDate : new Date().getTime()) +
         '%7D%2C%22param%22%3A%7B%22search%22%3A%7B%221_call%22%3A%7B%22callid%22%3A%5B%22' +
         (this.callSegmentsAndRecording?.sipCallId || this.callLogs.sipCallId) +
         '%22%5D%7D%7D%7D%7D=';
      return link;
   }

   get graylogSipCallIdLogsLink(): string {
      let link =
         'https://ndt-graylog.lcs.com/search?q=CallId%3A' +
         (this.callSegmentsAndRecording?.sipCallId || this.callLogs.sipCallId);
      if (this.callLogs.startTime && this.callLogs.endTime) {
         link +=
            '&rangetype=absolute&from=' +
            this.datePipe.transform(new Date(this.adjustedStartTimeDate), 'yyyy-MM-ddTHH:mm:ss.SSSZ') +
            '&to=' +
            this.datePipe.transform(new Date(this.adjustedEndTimeDate), 'yyyy-MM-ddTHH:mm:ss.SSSZ');
      } else if (this.callLogs.startTime) {
         let secondsAgoTheCallStarted: number;
         secondsAgoTheCallStarted = (new Date().getTime() - new Date(this.callLogs.startTime).getTime()) / 1000;
         secondsAgoTheCallStarted += 30;
         link += '&rangetype=relative&from=' + secondsAgoTheCallStarted;
      }
      return link;
   }

   get adjustedEndTimeDate(): number {
      return new Date(this.callLogs.endTime || new Date()).getTime() + 30000;
   }

   get adjustedStartTimeDate(): number {
      return new Date(this.callLogs.startTime || new Date()).getTime() - 30000;
   }

   get providerDetails(): string {
      const sourceProvider = this.callSegmentsAndRecording?.sourceProviderId ?? this.callLogs?.sourceProviderId ?? null;
      const destinationProvider =
         this.callSegmentsAndRecording?.destinationProviderId ?? this.callLogs?.destinationProviderId ?? null;

      if (sourceProvider == null && destinationProvider == null) {
         const legacyProvider = this.callSegmentsAndRecording?.providerId ?? this.callLogs?.providerId ?? null;
         return getProviderDisplayName(legacyProvider);
      }

      const providerDetails = [
         { label: 'Source', provider: getProviderDisplayName(sourceProvider) },
         { label: 'Destination', provider: getProviderDisplayName(destinationProvider) },
      ]
         .filter((providerDetail) => providerDetail.provider)
         .map((providerDetail) => `${providerDetail.label}: ${providerDetail.provider}`);

      return providerDetails.join(' / ');
   }

   get ipDetails(): string {
      const sourceIpAddress = this.callSegmentsAndRecording?.sourceIpAddress ?? this.callLogs?.sourceIpAddress ?? null;
      const destinationIpAddress =
         this.callSegmentsAndRecording?.destinationIpAddress ?? this.callLogs?.destinationIpAddress ?? null;

      const ipDetails = [
         { label: 'Source', ipAddress: sourceIpAddress },
         { label: 'Destination', ipAddress: destinationIpAddress },
      ]
         .filter((ipDetail) => ipDetail.ipAddress)
         .map((ipDetail) => `${ipDetail.label}: ${ipDetail.ipAddress}`);
      return ipDetails.join(' / ');
   }

   onCloseClick(): void {
      this.destroyed$.next();
      this.dialogRef.close();
   }

   openLink(url: string) {
      window.open(url, '_blank');
   }

   copySipIdToClipboard(sipId: string) {
      const copied: boolean = this.clipboard.copy(sipId);

      if (copied) {
         this.snackBar.open('Copied to Clipboard');
      }
   }

   onAdvancedSectionToggled(expanded: boolean): void {
      this.store.dispatch(saveExpandCallDetailAdvancedSection({ expandCallDetailAdvancedSection: expanded }));
   }

   onHangupActiveCall(activeCall: ActiveCallsRegisterListItem) {
      if (this.registerType === ActiveCallRegisterType.SystemActive) {
         this.store.dispatch(SystemActiveCallsRegisterActions.hangupActiveCall({ activeCall: activeCall }));
      } else if (this.registerType === ActiveCallRegisterType.AccountActive) {
         this.store.dispatch(AccountActiveCallsRegisterActions.hangupActiveCall({ activeCall: activeCall }));
      }
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onRowClick(row: TableRow) {
      if (row.isDial || !row.hasDials) return;
      row.isExpanded = !row.isExpanded;
      this.flattenedRows = this.buildFlattenedRows(
         this.callSegmentsWithAssociatedCallDetail,
         this.allQueueHistoryItems
      );
   }

   // The flattened segments table can include rows pulled in from related call details, e.g. a
   // callback attempt is a separate call leg with its own recording, and the segments/dials that
   // belong to it don't line up with the recording loaded in this dialog. Only a row's own
   // original call leg (matching this.entityId), and "agent" dials (which genuinely occur while
   // this call is on hold/ringing, so are part of this same recording), are safe to seek to.
   canSeekToRow(row: TableRow): boolean {
      if (!this.canShowRecordingFile) {
         return false;
      }
      if (row.isDial) {
         return row.dial.type === 'agent' && row.parentCallDetailId === this.entityId;
      }
      return row.parentCallDetailId === this.entityId;
   }

   seekRecordingToRow(row: TableRow): void {
      if (!this.canSeekToRow(row)) {
         return;
      }
      if (!this.audioPlayerRef) {
         return;
      }
      const segmentStart = row.isDial ? row.dial?.dialStartTime : row.startDate;
      const recordingStart = this.callSegmentsAndRecording?.startTime ?? this.callLogs?.startTime;
      if (!segmentStart || !recordingStart) {
         return;
      }
      const offsetSeconds = (new Date(segmentStart).getTime() - new Date(recordingStart).getTime()) / 1000;
      if (!isFinite(offsetSeconds)) {
         return;
      }

      this.audioPlayerRef.seekTo(offsetSeconds);
      try {
         this.audioPlayerRef.playAudio();
      } catch {
         // Autoplay can be blocked by the browser in rare cases; the seek still succeeds and the
         // user can press play manually.
      }
   }

   fetchHideRecordingDate(accountId: number): void {
      this.accountDetailApiService
         .getAccountDetails(accountId)
         .pipe(take(1))
         .subscribe((result) => {
            if (result.data) {
               this.hideRecordingDate = setHideRecordingDate(result.data.account.callRetentionPolicyTypeId);
            }
         });
   }

   buildCallSegmentsWithAssociatedCallDetails() {
      const dataSource = this.callSegmentsAndRecording ?? this.callLogs;

      const allCallDetails = [
         dataSource,
         ...(this.relatedCallDetails ?? []),
         ...(this.callLogs?.relatedCallDetails ?? []),
         ...(dataSource.queueHistoryCallDetailsConnection?.items?.reduce(
            (acc: CallDetail[], h: QueueHistoryCallDetail) => [
               ...acc,
               ...((h.queueHistory as QueueHistory).queueHistoryCallDetailsConnection?.items?.map(
                  (qhcd: QueueHistoryCallDetail) => qhcd.callDetail
               ) ?? []),
            ],
            []
         ) ?? []),
      ]
         .filter((cd): cd is CallDetail => Boolean(cd))
         .filter((cd, index, self) => index === self.findIndex((c) => c.callDetailId === cd.callDetailId));

      this.allQueueHistoryItems = [
         ...(dataSource.queueHistoryCallDetailsConnection?.items ?? []),
         ...(this.relatedCallDetails?.reduce(
            (acc: QueueHistoryCallDetail[], cd) => [...acc, ...(cd.queueHistoryCallDetailsConnection?.items ?? [])],
            []
         ) ?? []),
         ...(this.callLogs?.relatedCallDetails?.reduce(
            (acc: QueueHistoryCallDetail[], cd) => [...acc, ...(cd.queueHistoryCallDetailsConnection?.items ?? [])],
            []
         ) ?? []),
      ];
      const segments: CallSegmentRow[] = allCallDetails
         .reduce((acc: CallSegmentRow[], callDetail: CallDetail) => {
            const rows =
               callDetail.callSegmentsConnection?.items
                  .filter(
                     (segment: CallSegment) =>
                        !(
                           segment.extension?.extensionType?.name === ExtensionTypes.CallQueue &&
                           segment.sequence === 1 &&
                           callDetail.callDirectionTypeId === 'System'
                        )
                  )
                  .map((segment: CallSegment) => {
                     return this.buildCallSegmentRow(segment, this.allQueueHistoryItems, callDetail.callDetailId);
                  }) ?? [];
            acc.push(...rows);
            return acc;
         }, [] as CallSegmentRow[])
         .filter((row): row is CallSegmentRow => Boolean(row))
         .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

      this.callSegmentsWithAssociatedCallDetail = segments;
      this.flattenedRows = this.buildFlattenedRows(segments, this.allQueueHistoryItems);
   }

   buildFlattenedRows(segments: CallSegmentRow[], queueHistories: QueueHistoryCallDetail[]): TableRow[] {
      return segments.reduce((rows: TableRow[], segment: CallSegmentRow) => {
         rows.push(segment);
         if (!segment.isExpanded) {
            return rows;
         }
         segment.dials?.forEach((dial: SegmentDial) => {
            // Dials that occur after the callback connected belong to the *answered* call's own
            // recording, not this segment's — attribute them there so seek eligibility follows the
            // recording that actually contains that audio.
            const belongsToAnsweredCall =
               segment.answeredCallDetailId != null &&
               segment.answeredCallBoundaryTime != null &&
               dial.dialStartTime != null &&
               new Date(dial.dialStartTime).getTime() >= new Date(segment.answeredCallBoundaryTime).getTime();
            rows.push({
               isDial: true,
               dial,
               parentCallDetailId: belongsToAnsweredCall ? segment.answeredCallDetailId! : segment.parentCallDetailId,
            });
            if (dial.type === 'callback') {
               const isLatestCallbackDial = segment.dials
                  ?.filter((d) => d.type === 'callback')
                  .every((cb) => cb.dialStartTime <= dial.dialEndTime);
               const callerReenteredQueueAfterCallbackDial = queueHistories.some(
                  (qhcd) =>
                     new Date(qhcd.queueHistory.callbackCompleteDateTime).getTime() >
                        new Date(dial.dialEndTime).getTime() &&
                     qhcd.queueHistory.callDispositionId !== QueueHistoryDisposition.CallbackRejected
               );
               if (isLatestCallbackDial && callerReenteredQueueAfterCallbackDial) {
                  rows.push({
                     isDial: true,
                     dial: {
                        type: 'callbackrequeued',
                        dialStartTime: dial.dialEndTime,
                     },
                     parentCallDetailId: segment.parentCallDetailId,
                  });
               }
            }
         });

         return rows;
      }, []);
   }

   getDialDuration(dial: QueueAgentDial | QueueCallbackDial): number {
      const duration = (new Date(dial?.dialEndTime).getTime() - new Date(dial?.dialStartTime).getTime()) / 1000;
      return duration >= 0 ? duration : 0;
   }

   private getIsPhoneCallThatWentDirectlyToSourceExtensionFromCallData(item): boolean {
      return (
         item.sourceExtension?.extensionId === item.destinationExtension?.extensionId &&
         item.callDirectionTypeId === CallDirectionTypes.Internal &&
         item.destinationNumber == item.sourceExtension?.number &&
         item.destinationNumber == item.destinationExtension?.number
      );
   }

   private buildCallSegmentRow(
      segment: CallSegment,
      allQueueHistoryItems: QueueHistoryCallDetail[],
      parentCallDetailId: number
   ): CallSegmentRow {
      let callbackRequestedDial: CallbackRequestedSegmentDial[] = [];
      const queueHistoryCallDetail = allQueueHistoryItems.find(
         (h: QueueHistoryCallDetail) => h.queueHistory.queueExtensionId === segment.extension?.extensionId
      );
      const agentDials = queueHistoryCallDetail?.queueHistory.queueAgentDialsConnection?.items
         .filter((dial: QueueAgentDial) => {
            return dial.dialStartTime >= segment.startDate;
         })
         .map(
            (dial): AgentSegmentDial => ({
               ...dial,
               type: 'agent',
               duration: this.getDialDuration(dial),
            })
         );
      const callbackDials = queueHistoryCallDetail?.queueHistory.queueCallbackDialsConnection?.items
         .filter((cb: QueueCallbackDial) => {
            return cb.dialStartTime >= segment.startDate;
         })
         .map(
            (cb): CallbackSegmentDial => ({
               ...cb,
               type: 'callback',
               duration: this.getDialDuration(cb),
            })
         );
      const isCallQueueSegment = segment.extension?.extensionType?.name === ExtensionTypes.CallQueue;
      const hasRequestedCallback =
         segment.endDate != null &&
         !!allQueueHistoryItems.find((qhcd) => qhcd.queueHistory.queueExtensionId === segment.extension?.extensionId)
            ?.queueHistory.callbackRequestDateTime;
      if (isCallQueueSegment && hasRequestedCallback) {
         callbackRequestedDial = [
            {
               type: 'callbackrequested',
               dialStartTime: segment.endDate,
            },
         ];
      }
      const allDials = [...(agentDials ?? []), ...(callbackDials ?? []), ...(callbackRequestedDial ?? [])].sort(
         (a, b) => new Date(a.dialStartTime).getTime() - new Date(b.dialStartTime).getTime()
      );

      // If the callback was answered, that answer is a separate CallDetail (its own recording).
      let answeredCallDetailId: number | undefined;
      let answeredCallBoundaryTime: string | undefined;
      if (callbackDials && callbackDials.length > 0 && queueHistoryCallDetail) {
         const latestCallbackDial = callbackDials.reduce((latest, d) =>
            !latest || new Date(d.dialEndTime).getTime() > new Date(latest.dialEndTime).getTime() ? d : latest
         );
         if (latestCallbackDial?.dialEndTime) {
            // The other call leg's callDetailId isn't on these top-level connection items
            const siblingCallDetailId = queueHistoryCallDetail.queueHistory.queueHistoryCallDetailsConnection?.items
               ?.map((sibling) => sibling.callDetailId)
               .find((id) => id != null && id !== parentCallDetailId);
            if (siblingCallDetailId != null) {
               answeredCallDetailId = siblingCallDetailId;
               answeredCallBoundaryTime = latestCallbackDial.dialEndTime;
            }
         }
      }

      return {
         ...segment,
         isDial: false,
         dials: allDials,
         hasDials: allDials.length > 0,
         hasCallback: !!(callbackDials && callbackDials.length > 0),
         isExpanded: false,
         parentCallDetailId,
         answeredCallDetailId,
         answeredCallBoundaryTime,
      } satisfies CallSegmentRow;
   }
}
